export interface SubCheckTemplate {
    key: string;
    label: string;
    group: "FE" | "NFE" | "SS" | "MD";
    groupTitle: string;
    hasValueField?: boolean;
}

export const SUB_CHECKS: SubCheckTemplate[] = [
    { key: "fecentrepass1", label: "FE - Centre 1st Pass", group: "FE", groupTitle: "FE (Ferrous) Check" },
    { key: "fecentrepass2", label: "FE - Centre 2nd Pass", group: "FE", groupTitle: "FE (Ferrous) Check" },
    { key: "nfecentrepass1", label: "NFE - Centre 1st Pass", group: "NFE", groupTitle: "NFE (Non-Ferrous) Check" },
    { key: "nfecentrepass2", label: "NFE - Centre 2nd Pass", group: "NFE", groupTitle: "NFE (Non-Ferrous) Check" },
    { key: "sscentrepass1", label: "SS - Centre 1st Pass", group: "SS", groupTitle: "SS (Stainless Steel) Check" },
    { key: "sscentrepass2", label: "SS - Centre 2nd Pass", group: "SS", groupTitle: "SS (Stainless Steel) Check" },
    { key: "mdsensitivity", label: "MD Sensitivity Check", group: "MD", groupTitle: "M.D. Sensitivity & Rejection in Time", hasValueField: true }
];

export interface CCPCheckPointTabRow {
    id: string;
    rowNumber: number;
    subChecks: {
        key: string;
        label: string;
        group: string;
        criteria: "Okay" | "Not Okay";
        sensitivityValue?: string;
        defectRemarks?: string;
        actionTaken?: string;
        notifiedDepartment?: string;
        deviationStatus?: "New" | "Action Taken" | "Closed" | "Escalated" | "None";
        escalated?: "Yes" | "No";
        dbId?: string;
    }[];
}

export interface SievesMagnetsItemTemplate {
    id: string;
    description: string;
    frequency: '4hrs' | '8hrs';
    plantGroup: 'New Plant' | 'Old Plant';
}

export const CCP_OPRP_LINES = [
    "Line-1",
    "Line-2",
    "Line-3",
    "Line-4",
    "Line-5",
    "Line-6",
    "Line-7",
    "Line-8",
    "FFS"
] as const;

export const SITES = [
    "Rajpura",
    "Phillaur",
    "Indore",
    "Tahliwal"
] as const;

export const SHIFTS = [
    "Shift 1",
    "Shift 2",
    "Shift 3"
] as const;

export const PARAMETER_VERIFICATIONS = [
    "CCP & OPRP",
    "Sieves and Magnets"
] as const;

export const FREQUENCIES = [
    "4hrs",
    "8hrs"
] as const;

export const ACCEPTANCE_RESPONSES = [
    "OK",
    "Line Not Operational",
    "Plant Not Operational"
] as const;

export const DEVIATION_STATUSES = [
    "None",
    "New",
    "Action Taken",
    "Closed",
    "Escalated"
] as const;

/** Checkpoints list mapped by Line for CCP & OPRP */
export const LINE_CHECKPOINTS_MAP: Record<string, string[]> = {
    "Line-1": ["OPRP-1 : Rotary (Metal Detector)", "OPRP-2 : Rotary (Metal Detector)", "CCP : Packing (Metal Detector)"],
    "Line-2": ["OPRP-1 : Rotary (Metal Detector)", "OPRP : Rework (Metal Detector)", "CCP : Packing (Metal Detector)"],
    "Line-3": ["OPRP-1 : Rotary (Metal Detector)", "OPRP : Offline (Metal Detector)", "CCP : Packing (Metal Detector)"],
    "Line-4": ["OPRP-1 : Rotary (Metal Detector)", "OPRP : Rework (Metal Detector)", "CCP : Packing (Metal Detector)"],
    "Line-5": ["OPRP-1 : Rotary (Metal Detector)", "OPRP-2 : Shell (Metal Detector)", "OPRP-3 : Rework (Metal Detector)", "CCP - Post Cooling Tunnel (Metal Detector)"],
    "Line-6": ["OPRP-1 : Rotary (Metal Detector)", "OPRP-2 : Rework (Metal Detector)", "CCP - Post Cooling Tunnel (Metal Detector)"],
    "Line-7": ["OPRP-1 : Rotary (Metal Detector)", "OPRP-2 : Shell (Metal Detector)", "OPRP-3 : Rework (Metal Detector)", "CCP (Metal Detector)"],
    "Line-8": ["OPRP-1 : Rotary (Metal Detector)", "OPRP-2 : Rotary (Metal Detector)", "CCP : Packing (Metal Detector)"],
    "FFS": ["CCP-1 : Packing (Metal Detector)", "CCP-2 : Packing (Metal Detector)"]
};

/** Sieves & Magnets Checklists grouped by plant location and frequency */
export const SIEVES_MAGNETS_ITEMS: SievesMagnetsItemTemplate[] = [
    // New Plant (Lines 5, 6, 7) - 8hrs
    { id: "NP_8_1", description: "Sugar Sifter Pre-Mixing Area", frequency: "8hrs", plantGroup: "New Plant" },
    { id: "NP_8_2", description: "Sugar Sifter RM Area", frequency: "8hrs", plantGroup: "New Plant" },
    { id: "NP_8_3", description: "Maida Sifter Sieve Double Decker L-5&7", frequency: "8hrs", plantGroup: "New Plant" },
    { id: "NP_8_4", description: "Maida Sifter Sieve Double Decker L-6", frequency: "8hrs", plantGroup: "New Plant" },

    // New Plant (Lines 5, 6, 7) - 4hrs
    { id: "NP_4_1", description: "Biscuits Dust 1 (L-5&7)", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_2", description: "Biscuits Dust 2 (L-6)", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_3", description: "Chemical Sifter 1", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_4", description: "Chemical Sifter 2", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_5", description: "Chemical Sifter 3 (Cocoa powder)", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_6", description: "Chemical Sifter 4 (SMP)", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_7", description: "Invert Syrup - Bucket Filter", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_8", description: "Black jack - Bucket Filter", frequency: "4hrs", plantGroup: "New Plant" },
    { id: "NP_4_9", description: "Sugar Grinding Room", frequency: "4hrs", plantGroup: "New Plant" },

    // Old Plant (Lines 1, 2, 3, 4, 8) - 8hrs
    { id: "OP_8_1", description: "Sugar Sifter 1", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_2", description: "Sugar Sifter 2", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_3", description: "Sugar grinder mesh - Line 1", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_4", description: "Sugar grinder mesh - Line 2", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_5", description: "Sugar grinder mesh - Line 3", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_6", description: "Sugar grinder mesh - Line 4", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_7", description: "Maida Sifter Sieve - Line 5", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_8", description: "Maida Sifter Sieve - Line 6", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_9", description: "Maida Sifter Sieve - Line 3", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_10", description: "Maida Sifter Sieve - Line 4", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_11", description: "Sugar Mesh at rotary line-1", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_12", description: "Biscuits Dust 1", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_13", description: "Biscuits Dust 2", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_14", description: "Chemical Sifter 1", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_15", description: "Chemical Sifter 3", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_16", description: "Chemical Sifter 5", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_17", description: "Atta shifter", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_18", description: "Maida Sieve for sampling", frequency: "8hrs", plantGroup: "Old Plant" },
    { id: "OP_8_19", description: "Invert Syrup - Bucket Filter (Every Batch Change)", frequency: "8hrs", plantGroup: "Old Plant" },

    // Old Plant (Lines 1, 2, 3, 4, 8) - 4hrs
    { id: "OP_4_1", description: "Maida hopper magnet line-1", frequency: "4hrs", plantGroup: "Old Plant" },
    { id: "OP_4_2", description: "Maida hopper magnet line-2", frequency: "4hrs", plantGroup: "Old Plant" },
    { id: "OP_4_3", description: "Maida hopper magnet line-3", frequency: "4hrs", plantGroup: "Old Plant" },
    { id: "OP_4_4", description: "Maida hopper magnet line-4", frequency: "4hrs", plantGroup: "Old Plant" },
    { id: "OP_4_5", description: "Sugar grinder Magnet", frequency: "4hrs", plantGroup: "Old Plant" },
    { id: "OP_4_6", description: "Biscuit dust magnet 1", frequency: "4hrs", plantGroup: "Old Plant" },
    { id: "OP_4_7", description: "Biscuit dust magnet 2", frequency: "4hrs", plantGroup: "Old Plant" }
];
